import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://cbackend.xraidigital.com/api/technician/finance/expenses";

const BRAND = "#11a8a4";
const BRAND_DARK = "#0d8a86";
const BRAND_LIGHT = "#e6f7f7";
const BRAND_MID = "#b2e6e5";

const STATUS_CONFIG = {
  PENDING:  { label: "Pending",  bg: "#fff7e6", color: "#b45309", dot: "#f59e0b" },
  APPROVED: { label: "Approved", bg: "#e6f7f0", color: "#065f46", dot: "#10b981" },
  REJECTED: { label: "Rejected", bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" },
};

const CATEGORY_ICONS = {
  TRAVEL: "✈️",
  FOOD:   "🍽️",
  STAY:   "🏨",
  OTHER:  "📦",
};

// ─── Badge ───────────────────────────────────────────────────────────────────
function Badge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 99,
      background: cfg.bg, color: cfg.color,
      fontSize: 12, fontWeight: 600,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
      {cfg.label}
    </span>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
function Modal({ expense, onClose, onApprove, onReject, loading }) {
  if (!expense) return null;
  const cat = expense.category || "OTHER";
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520,
        boxShadow: "0 24px 48px rgba(0,0,0,0.18)", overflow: "hidden",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: BRAND, padding: "20px 24px", color: "#fff" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 22, marginBottom: 4 }}>{CATEGORY_ICONS[cat]} {cat}</div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>₹{parseFloat(expense.amount || 0).toLocaleString()}</div>
            </div>
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
              borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 18, lineHeight: 1,
            }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>
          <Row label="Status"><Badge status={expense.status} /></Row>
          <Row label="Description">{expense.description || "—"}</Row>
          <Row label="Date">{expense.date || expense.created_at?.slice(0,10) || "—"}</Row>
          <Row label="Submitted by">{expense.technician_name || expense.submitted_by || "—"}</Row>
          <Row label="Camp">
            {expense.camp_name || "—"}
          </Row>
          <Row label="Source">
            {expense.source || "—"}
          </Row>
          {expense.reviewed_at && (
              <Row label="Reviewed At">
                  {new Date(expense.reviewed_at).toLocaleString()}
              </Row>
          )}
          {expense.reviewed_by_name && (
              <Row label="Reviewed By">
                  {expense.reviewed_by_name}
              </Row>
          )}

          {expense.rejection_reason && (
              <Row label="Reason">
                  {expense.rejection_reason}
              </Row>
          )}
          {(expense.attachment || expense.attachment_url) && (
            <Row label="Attachment">
              <a
                href={expense.attachment || expense.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "8px 14px",
                  background: BRAND,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                📎 View Attachment
              </a>
            </Row>
          )}
        </div>

        {/* Actions */}
        {expense.status === "PENDING" &&
        expense.source?.toUpperCase() !== "COMPANY_FIXED" && (
          <div
            style={{
              display: "flex",
              gap: 10,
              padding: "0 24px 20px",
            }}
          >
            <button
              onClick={() => onApprove(expense.id)}
              disabled={loading}
              style={{
                flex: 1,
                padding: "10px 0",
                borderRadius: 10,
                border: "none",
                background: BRAND,
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                transition: "background 0.15s",
              }}
              onMouseEnter={e =>
                !loading && (e.target.style.background = BRAND_DARK)
              }
              onMouseLeave={e =>
                !loading && (e.target.style.background = BRAND)
              }
            >
              {loading ? "Processing…" : "✓ Approve"}
            </button>

            <button
              onClick={() => onReject(expense)}
              disabled={loading}
              style={{
                flex: 1,
                padding: "10px 0",
                borderRadius: 10,
                border: "2px solid #ef4444",
                background: "#fff",
                color: "#ef4444",
                fontWeight: 700,
                fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? "Processing…" : "✕ Reject"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "flex-start" }}>
      <span style={{ minWidth: 110, color: "#6b7280", fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: 500, color: "#111827", fontSize: 14 }}>{children}</span>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, bg, icon }) {
  return (
    <div style={{
      background: bg, borderRadius: 12, padding: "16px 18px",
      display: "flex", justifyContent: "space-between", alignItems: "center",
    }}>
      <div>
        <div style={{ fontSize: 12, color, fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
      </div>
      <div style={{ fontSize: 26 }}>{icon}</div>
    </div>
  );
}

// ─── Expense Card ─────────────────────────────────────────────────────────────
function ExpenseCard({ expense, onClick }) {
  const [hovered, setHovered] = useState(false);
  const cat = expense.category || "OTHER";
  return (
    <div
      onClick={() => onClick(expense)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? BRAND_LIGHT : "#fff",
        border: `1.5px solid ${hovered ? BRAND : "#e5e7eb"}`,
        borderRadius: 12, padding: "16px 18px", cursor: "pointer",
        transition: "all 0.18s", display: "flex", justifyContent: "space-between", alignItems: "center",
        boxShadow: hovered ? `0 4px 16px rgba(17,168,164,0.12)` : "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10,
          background: hovered ? BRAND_MID : "#f3f4f6",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, transition: "background 0.18s",
        }}>
          {CATEGORY_ICONS[cat]}
        </div>
        <div>
        <div
            style={{
                fontWeight: 600,
                color: "#111827",
                marginBottom: 3,
            }}
        >
            {expense.description || cat}
        </div>

        <div
            style={{
                color: "#9ca3af",
                fontSize: 12,
            }}
        >
            #{expense.id} · {expense.created_at?.slice(0,10)}
        </div>

        {(expense.attachment || expense.attachment_url) && (
            <div
                style={{
                    color: BRAND,
                    fontSize: 12,
                    marginTop: 5,
                    fontWeight: 600,
                }}
            >
                📎 Attachment Available
            </div>
        )}
    </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: 700, color: BRAND_DARK, fontSize: 16 }}>
          ₹{parseFloat(expense.amount || 0).toLocaleString()}
        </div>
        <div style={{ marginTop: 4 }}><Badge status={expense.status} /></div>
      </div>
    </div>
  );
}

// ─── Filter Pill ──────────────────────────────────────────────────────────────
function Pill({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: "6px 14px", borderRadius: 99, fontSize: 13, fontWeight: 600,
      border: active ? "none" : "1.5px solid #e5e7eb",
      background: active ? BRAND : "#fff",
      color: active ? "#fff" : "#374151",
      cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
    }}>
      {children}
    </button>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function ExpenseManager() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [toast, setToast] = useState(null);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectExpenseId, setRejectExpenseId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = BASE_URL + "/";
      const params = [];
      if (statusFilter) params.push(`status=${statusFilter}`);
      if (categoryFilter) params.push(`category=${categoryFilter}`);
      if (params.length) url += "?" + params.join("&");
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setExpenses(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/${id}/approve/`, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast("Expense approved successfully");
      setSelected(null);
      fetchExpenses();
    } catch (e) {
      showToast(e.message, false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
      localStorage.clear();
      sessionStorage.clear();

      navigate("/login");
  };

  const handleReject = (expense) => {
      setRejectExpenseId(expense.id);
      setRejectModal(true);
  };

  const confirmReject = async () => {
      setActionLoading(true);

      const token = localStorage.getItem("token");

      try {
          const res = await fetch(
              `${BASE_URL}/${rejectExpenseId}/reject/`,
              {
                  method: "POST",
                  headers: {
                      "Content-Type": "application/json",
                      "Authorization": `Token ${token}`,
                  },
                  body: JSON.stringify({
                      rejection_reason: rejectionReason,
                  }),
              }
          );

          if (!res.ok) {
              throw new Error(`HTTP ${res.status}`);
          }

          showToast("Expense rejected");

          setRejectModal(false);
          setRejectExpenseId(null);
          setRejectionReason("");
          setSelected(null);

          fetchExpenses();

      } catch (e) {
          showToast(e.message, false);
      } finally {
          setActionLoading(false);
      }
  };

  const handleCardClick = async (expense) => {
    try {
      const res = await fetch(`${BASE_URL}/${expense.id}/`);
      if (!res.ok) throw new Error();
      const detail = await res.json();
      setSelected(detail);
    } catch {
      setSelected(expense);
    }
  };

  // Stats
  const total    = expenses.length;
  const pending  = expenses.filter(e => e.status === "PENDING").length;
  const approved = expenses.filter(e => e.status === "APPROVED").length;
  const totalAmt = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ background: BRAND, padding: "0 0 0 0" }}>
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "24px 20px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, marginBottom: 4 }}>Finance Portal</div>
              <h1 style={{ color: "#fff", margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-0.5px" }}>
                Expense Management
              </h1>
            </div>
            <div
                style={{
                    display: "flex",
                    gap: 10,
                }}
            >
                <button
                    onClick={fetchExpenses}
                    style={{
                        background: "rgba(255,255,255,0.18)",
                        border: "none",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 16px",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 13,
                    }}
                >
                    ↻ Refresh
                </button>

                <button
                    onClick={handleLogout}
                    style={{
                        background: "#ef4444",
                        border: "none",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 16px",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 13,
                    }}
                >
                    Logout
                </button>
            </div>
          </div>

          {/* Stats Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
            <MiniStat label="Total" value={total} />
            <MiniStat label="Pending" value={pending} accent="#fbbf24" />
            <MiniStat label="Approved" value={approved} accent="#34d399" />
            <MiniStat label="Amount" value={`₹${totalAmt >= 1000 ? (totalAmt/1000).toFixed(1)+"k" : totalAmt}`} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "10px 20px", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Pill active={!statusFilter} onClick={() => setStatusFilter("")}>All Status</Pill>
            {["PENDING","APPROVED","REJECTED"].map(s => (
              <Pill key={s} active={statusFilter===s} onClick={() => setStatusFilter(s)}>
                {STATUS_CONFIG[s].label}
              </Pill>
            ))}
          </div>
          <div style={{ width: 1, background: "#e5e7eb", alignSelf: "stretch" }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Pill active={!categoryFilter} onClick={() => setCategoryFilter("")}>All Types</Pill>
            {["TRAVEL","FOOD","STAY","OTHER"].map(c => (
              <Pill key={c} active={categoryFilter===c} onClick={() => setCategoryFilter(c)}>
                {CATEGORY_ICONS[c]} {c}
              </Pill>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "20px 20px 40px" }}>
        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "12px 16px", color: "#991b1b", marginBottom: 16, fontSize: 14,
          }}>
            ⚠️ {error} — <button onClick={fetchExpenses} style={{ color: BRAND, border: "none", background: "none", cursor: "pointer", fontWeight: 600 }}>Retry</button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
            <div>Loading expenses…</div>
          </div>
        ) : expenses.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🗂️</div>
            <div style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}>No expenses found</div>
            <div style={{ fontSize: 13 }}>Try adjusting your filters</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 4 }}>
              {expenses.length} expense{expenses.length !== 1 ? "s" : ""} found
            </div>
            {expenses.map(e => (
              <ExpenseCard key={e.id} expense={e} onClick={handleCardClick} />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal
        expense={selected}
        onClose={() => setSelected(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        loading={actionLoading}
      />

      {rejectModal && (
      <div
          style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.45)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2000,
          }}
      >
          <div
              style={{
                  width: 450,
                  background: "#fff",
                  borderRadius: 12,
                  padding: 20,
              }}
          >
              <h3>Reject Expense</h3>

              <div style={{ marginBottom: 15 }}>
                  <label>Rejection Reason</label>

                  <textarea
                      value={rejectionReason}
                      onChange={(e) =>
                          setRejectionReason(e.target.value)
                      }
                      rows={4}
                      style={{
                          width: "100%",
                          marginTop: 5,
                      }}
                  />
              </div>


              <div
                  style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 10,
                  }}
              >
                  <button
                      onClick={() => {
                          setRejectModal(false);
                          setRejectionReason("");
                      }}
                  >
                      Cancel
                  </button>

                  <button
                      onClick={confirmReject}
                      style={{
                          background: "#ef4444",
                          color: "#fff",
                          border: "none",
                          padding: "8px 18px",
                          borderRadius: 8,
                      }}
                  >
                      Reject
                  </button>
              </div>
          </div>
      </div>
  )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.ok ? BRAND : "#ef4444",
          color: "#fff", padding: "12px 20px", borderRadius: 10,
          fontWeight: 600, fontSize: 14, zIndex: 2000,
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          animation: "fadeIn 0.2s ease",
        }}>
          {toast.ok ? "✓" : "✕"} {toast.msg}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.15)", borderRadius: 10,
      padding: "10px 12px", textAlign: "center",
    }}>
      <div style={{ color: accent || "rgba(255,255,255,0.75)", fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>{value}</div>
    </div>
  );
}